import { PECreditModelLabels } from '@shared/constants';

/**
 * @typedef {'ClassicFICO' | 'VantageScore4' | 'FICO10T'} CreditModel
 * @typedef {'fico' | 'vantageScore4' | 'fico10T'} CreditScoreKey
 * @typedef {{ value?: *, visible?: boolean, options?: { value: string }[] }} TemplateField
 * @typedef {Object<string, TemplateField>} TemplateBorrower
 * @typedef {{ creditModel?: CreditModel, vantageScore4?: number | null, fico10T?: number | null }} CreditModelRequestFields
 * @typedef {{ score: number | string | null, modelLabel: string }} CreditScoreSummary
 */

export const CLASSIC_FICO = 'ClassicFICO';

const SCORE_KEY_BY_CREDIT_MODEL = Object.freeze({
  ClassicFICO: 'fico',
  VantageScore4: 'vantageScore4',
  FICO10T: 'fico10T',
});

export const NEW_CREDIT_SCORE_KEYS = Object.freeze(
  Object.entries(SCORE_KEY_BY_CREDIT_MODEL)
    .filter(([creditModel]) => creditModel !== CLASSIC_FICO)
    .map(([, scoreKey]) => scoreKey),
);

export const CREDIT_MODEL_FIELD_KEYS = Object.freeze([
  'creditModel',
  ...NEW_CREDIT_SCORE_KEYS,
]);

/** The labels the pricer shows for the credit model fields, which read badly title-cased from their keys. */
export const CREDIT_FIELD_LABELS = Object.freeze({
  creditModel: 'Credit Model for Pricing',
  fico10T: PECreditModelLabels.FICO10T,
  vantageScore4: PECreditModelLabels.VantageScore4,
});

const isShown = field => Boolean(field?.visible);

/**
 * Whether the pricer shows a credit model field. A score the request never sends
 * (no model field shown) isn't shown either.
 *
 * @param {TemplateBorrower} borrower
 * @param {'creditModel' | 'vantageScore4' | 'fico10T'} key
 * @param {boolean} pricesOnCreditModels
 * @returns {boolean}
 */
export function isCreditFieldShown(borrower, key, pricesOnCreditModels) {
  return (
    Boolean(pricesOnCreditModels) &&
    isShown(borrower?.creditModel) &&
    isShown(borrower?.[key])
  );
}

const toScoreOrNull = value => {
  const score = Number(value);
  return value === '' || value == null || Number.isNaN(score) ? null : score;
};

/**
 * The selected credit model when the form shows it, offers it and shows its score;
 * otherwise Classic FICO. Mirrors the backend's `_credit_model_the_form_can_send`.
 *
 * @param {TemplateBorrower} borrower
 * @param {boolean} pricesOnCreditModels
 * @returns {CreditModel}
 */
function creditModelTheFormCanSend(borrower, pricesOnCreditModels) {
  const creditModelField = borrower?.creditModel;
  if (!pricesOnCreditModels || !isShown(creditModelField)) return CLASSIC_FICO;

  const selected = creditModelField.value;
  const offered = (creditModelField.options || []).map(option => option.value);
  if (!offered.includes(selected)) return CLASSIC_FICO;

  const scoreKey = SCORE_KEY_BY_CREDIT_MODEL[selected];
  if (selected !== CLASSIC_FICO && !isShown(borrower[scoreKey])) {
    return CLASSIC_FICO;
  }
  return selected;
}

/**
 * The borrower score the form must have before it can price.
 *
 * @param {TemplateBorrower} borrower
 * @param {boolean} pricesOnCreditModels
 * @returns {CreditScoreKey}
 */
export function requiredCreditScoreKey(borrower, pricesOnCreditModels) {
  return SCORE_KEY_BY_CREDIT_MODEL[
    creditModelTheFormCanSend(borrower, pricesOnCreditModels)
  ];
}

/**
 * The credit model fields a price request carries. None while the org doesn't price on
 * credit models or the model field is hidden, so the request prices on Classic FICO.
 *
 * @param {TemplateBorrower} borrower
 * @param {boolean} pricesOnCreditModels
 * @returns {CreditModelRequestFields}
 */
export function creditModelRequestFields(borrower, pricesOnCreditModels) {
  if (!pricesOnCreditModels || !isShown(borrower?.creditModel)) return {};

  return {
    creditModel: creditModelTheFormCanSend(borrower, pricesOnCreditModels),
    vantageScore4: toScoreOrNull(borrower.vantageScore4?.value),
    fico10T: toScoreOrNull(borrower.fico10T?.value),
  };
}

/**
 * The score the loan scenario header shows, with the name of the model it belongs to.
 * Orgs that don't price on credit models keep today's "FICO".
 *
 * @param {TemplateBorrower} borrower
 * @param {boolean} pricesOnCreditModels
 * @returns {CreditScoreSummary}
 */
export function creditScoreSummary(borrower, pricesOnCreditModels) {
  if (!pricesOnCreditModels) {
    return { score: borrower?.fico?.value ?? null, modelLabel: 'FICO' };
  }

  const creditModel = creditModelTheFormCanSend(borrower, pricesOnCreditModels);
  return {
    score: borrower?.[SCORE_KEY_BY_CREDIT_MODEL[creditModel]]?.value ?? null,
    modelLabel: PECreditModelLabels[creditModel],
  };
}
